import type { ViewportsConfigurations } from '../ViewportsConfigurations/types';
import type { TStackProviders } from './ViewerContext';

import { useEffect, useMemo, useRef } from 'react';

import {
  getHybridMammographySwapProvidersInWindow,
  useViewerMemoryConfig,
} from './useViewerMemoryConfig';
import { useViewerId } from 'hooks/useViewerId';
import { viewportIdToConfig } from './viewerUtils';
import { PixelDataSharedWorkerError } from 'modules/viewer/workers/pixelDataSharedWorkerTypes';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import analytics from 'modules/analytics';
import { globalContext } from 'modules/analytics/constants';
import { ImagingProviderLoadType } from '../ViewportDre/modules/imaging/BaseImagingProvider';
import { isWorkerLoadedImagingProvider } from '../ViewportDre/modules/imaging/WorkerLoadedImagingProvider';
import { useCaseScopedState } from 'hooks/useCaseScopedState';

export type ViewerMemoryConfigMap = Map<string, ImagingProviderLoadType>;

export type ViewerMemoryConfig = {
  memoryLimit: number;
  usedMemory: Nullable<number>;
  memoryConfig: ViewerMemoryConfigMap;
};

export type ViewerMemoryManagerProps = {
  stackProviders: TStackProviders;
  viewportsConfigurations: Nullable<ViewportsConfigurations>;
  areViewportsReady: boolean;
  hybridMammographyEnabled?: boolean;
  /** Total system RAM, when known reliably; caps the budget under VIEWER_MACHINE_MEMORY_BUDGET. */
  systemMemoryBytes?: Nullable<number>;
  viewerWindowCount?: number;
};

// a high-level hook to manage the side effects of loading and unloading
// based on a memory configuration calculated without side effects
function useViewerMemoryManagement(props: ViewerMemoryManagerProps): ViewerMemoryConfig {
  const { stackProviders, viewportsConfigurations, hybridMammographyEnabled = false } = props;
  const { memoryLimit, usedMemory, memoryConfig } = useViewerMemoryConfig(props);
  // Last load type applied per stack; survives renders where a stack is briefly absent from the config.
  const appliedLoadTypes = useRef<ViewerMemoryConfigMap>(new Map());

  const viewerId = useViewerId();
  const hybridSwapProviders = useMemo(
    () =>
      getHybridMammographySwapProvidersInWindow({
        stackProviders,
        viewportsConfigurations,
        hybridMammographyEnabled,
        viewerId,
      }),
    [stackProviders, viewportsConfigurations, hybridMammographyEnabled, viewerId]
  );

  useTrackMemoryManagement({ memoryLimit, usedMemory, memoryConfig });

  // inject memory config into providers so they know what to do with new images
  useEffect(() => {
    memoryConfig.forEach((loadType, stackSmid) => {
      const provider = stackProviders.get(stackSmid);
      if (provider == null) return;
      provider.loadType = loadType;
      // VX-4775: keep the worker's per-stack delivery mode in step with the load type just assigned,
      // so a stack at None stops having its frames transferred into the renderer and one promoted off
      // None starts receiving them again. Done here rather than in the transition effect below
      // because that effect ignores stacks absent from the previous config, and syncPixelDelivery is
      // a no-op unless the mode actually changed.
      if (isWorkerLoadedImagingProvider(provider)) {
        // VX-4775 normally keeps unhanged stacks cache-only. A hybrid swap target deliberately
        // allocated Full is the exception: it must be resident before the user switches views.
        if (loadType === ImagingProviderLoadType.Full && hybridSwapProviders.has(provider)) {
          provider.promoteToDelivery();
        }
        provider.syncPixelDelivery();
      }
    });
  }, [stackProviders, memoryConfig, hybridSwapProviders]);

  // throw imaging providers into error states that we don't have the space for
  // but are trying to render in a viewport
  useEffect(() => {
    memoryConfig.forEach((loadType, stackSmid) => {
      const provider = stackProviders.get(stackSmid);
      if (
        provider != null &&
        viewportsConfigurations != null &&
        loadType !== ImagingProviderLoadType.Full &&
        Object.entries(viewportsConfigurations).some(
          ([viewportId, vc]) =>
            vc?.stack?.smid === provider.linkSmid &&
            viewportIdToConfig(viewportId).windowId === viewerId
        )
      ) {
        provider.failLoading(PixelDataSharedWorkerError.NotEnoughMemory, true);
      }
    });
  }, [stackProviders, memoryConfig, viewportsConfigurations, viewerId]);

  // dynamically unload and load stacks' frames depending on what is on screen
  // and fits into the memory limits according to the memory configuration
  useEffect(() => {
    const previousLoadTypes = appliedLoadTypes.current;
    appliedLoadTypes.current = new Map(
      [...previousLoadTypes].filter(([stackSmid]) => stackProviders.has(stackSmid))
    );
    memoryConfig.forEach((loadType, stackSmid) =>
      appliedLoadTypes.current.set(stackSmid, loadType)
    );

    memoryConfig.forEach((loadType, stackSmid) => {
      const previousConfig = previousLoadTypes.get(stackSmid);
      if (previousConfig == null) return;

      const provider = stackProviders.get(stackSmid);
      if (provider == null) return;

      // VX-4775: a stack still waiting on its first drop into a viewport must not be given a *full*
      // delivery by ordinary memory-budget churn elsewhere (e.g. another unhanged stack disposing and
      // freeing budget). reloadAllFramesIntoMemory() always requests delivering pixels, which would
      // otherwise silently defeat the restriction the instant freed-up budget promotes this stack to
      // Full — the case the restriction exists to prevent.
      const isRestrictedUntilHanged =
        isWorkerLoadedImagingProvider(provider) && provider.isRestrictedUntilHanged();

      if (
        loadType === ImagingProviderLoadType.Full &&
        previousConfig !== ImagingProviderLoadType.Full &&
        !isRestrictedUntilHanged
      ) {
        provider.reloadAllFramesIntoMemory();
      }

      // Deliberately NOT gated on the restriction. `Initial` exists to make an unloaded stack feel
      // present for the cost of a single frame, and that trade still holds while restricted — the big
      // win is withholding *full* loads. The single frame goes out as its own `load-frame`, which the
      // worker services with an independent `cacheOnly: false` request, so the rest of the stack keeps
      // streaming cache-only underneath it.
      if (
        loadType === ImagingProviderLoadType.Initial &&
        previousConfig !== ImagingProviderLoadType.Initial
      ) {
        provider.loadFrameIntoMemory(provider.getActiveSlice('TWO_D_DRE'), {
          unloadOthers: true,
        });
      }

      if (
        loadType === ImagingProviderLoadType.None &&
        previousConfig !== ImagingProviderLoadType.None
      ) {
        provider.unloadAllFrames();
      }
    });
  }, [stackProviders, memoryConfig]);

  return { memoryLimit, usedMemory, memoryConfig };
}

function ViewerMemoryManager(props: ViewerMemoryManagerProps): React.ReactElement | null {
  useViewerMemoryManagement(props);
  return null;
}

function useTrackMemoryManagement({ memoryLimit, usedMemory, memoryConfig }: ViewerMemoryConfig) {
  const currentCaseId = useCurrentCaseId();

  const [maxMemoryUsedForThisCase, setMaxMemoryUsedForThisCase] = useCaseScopedState<number>(0);

  useEffect(() => {
    if (currentCaseId != null && usedMemory != null && maxMemoryUsedForThisCase < usedMemory) {
      setMaxMemoryUsedForThisCase(usedMemory);
    }
  }, [currentCaseId, usedMemory, maxMemoryUsedForThisCase, setMaxMemoryUsedForThisCase]);

  useEffect(() => {
    // Wait for state-update effect to complete if necessary.
    // This helps avoid double-calling addViewContext when a new larger memory config arrives.
    if (currentCaseId != null && usedMemory != null && maxMemoryUsedForThisCase >= usedMemory) {
      const pixelMemoryAnalytics = {
        used_memory: usedMemory,
        memory_limit: memoryLimit,
        used_ratio: usedMemory / memoryLimit,
        max_used: maxMemoryUsedForThisCase,
        // Memory limits are generally stable, except when flags are changed,
        // so we can keep it light and assume for analytics the memory limit
        // is the same as when it peaked.
        max_used_ratio: maxMemoryUsedForThisCase / memoryLimit,
        memory_configuration: Object.fromEntries(memoryConfig.entries()),
      };

      analytics?.addViewContext(globalContext.viewer.pixelMemory, pixelMemoryAnalytics);
    }
  }, [currentCaseId, memoryLimit, usedMemory, maxMemoryUsedForThisCase, memoryConfig]);
}

export { ViewerMemoryManager, useViewerMemoryManagement as __test_only__useViewerMemoryManagement };
